import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface OrganizationSettings {
  orgName: string;
  orgDescription: string;
  orgUrl: string;
  orgLogo: string;
  orgEmail: string;
  orgFoundingDate: string;
  socialFacebook: string;
  socialInstagram: string;
  socialTwitter: string;
  socialYoutube: string;
  socialTiktok: string;
  searchUrlTemplate: string;
}

// Cache for organization settings
let orgSettingsCache: OrganizationSettings | null = null;

export function useOrganizationSEO() {
  const [settings, setSettings] = useState<OrganizationSettings | null>(orgSettingsCache);
  const [loading, setLoading] = useState(!orgSettingsCache);

  useEffect(() => {
    if (!orgSettingsCache) {
      fetchSettings();
    }
  }, []);

  const fetchSettings = async () => {
    const { data } = await supabase
      .from('organization_seo_settings')
      .select('setting_key, setting_value');

    if (data) {
      const settingsMap = new Map(data.map(d => [d.setting_key, d.setting_value || '']));
      const parsed: OrganizationSettings = {
        orgName: settingsMap.get('org_name') || 'MotoReveal',
        orgDescription: settingsMap.get('org_description') || '',
        orgUrl: settingsMap.get('org_url') || 'https://motoreveal.com',
        orgLogo: settingsMap.get('org_logo') || '',
        orgEmail: settingsMap.get('org_email') || '',
        orgFoundingDate: settingsMap.get('org_founding_date') || '',
        socialFacebook: settingsMap.get('social_facebook') || '',
        socialInstagram: settingsMap.get('social_instagram') || '',
        socialTwitter: settingsMap.get('social_twitter') || '',
        socialYoutube: settingsMap.get('social_youtube') || '',
        socialTiktok: settingsMap.get('social_tiktok') || '',
        searchUrlTemplate: settingsMap.get('search_url_template') || '',
      };
      orgSettingsCache = parsed;
      setSettings(parsed);
    }
    setLoading(false);
  };

  const getSocialLinks = (): string[] => {
    if (!settings) return [];
    return [
      settings.socialFacebook,
      settings.socialInstagram,
      settings.socialTwitter,
      settings.socialYoutube,
      settings.socialTiktok,
    ].filter(Boolean);
  };

  return { settings, loading, getSocialLinks, refetch: fetchSettings };
}

// Clear cache function for admin updates
export function clearOrganizationSEOCache() {
  orgSettingsCache = null;
}
